/**
 * Hook for proposal mutation operations (create, approve, reject, etc.)
 */

import { type UseMutationOptions } from '@tanstack/react-query';

import type { ProposalAPIError } from '../core/errors';
import type {
  ActionPayload,
  BulkRejectRequest,
  BulkRejectResponse,
  CreateProposalRequest,
  ProposalDetail,
  ProposedAction,
  ReassignProposalRequest,
  RejectProposalRequest,
  UpdatePayloadRequest,
} from '../core/types';
import {
  useApproveProposal,
  useBulkRejectProposals,
  useCreateProposal,
  useDeleteProposal,
  useReassignProposal,
  useRejectProposal,
  useUpdateActionPayload,
} from '../query/mutations';

/**
 * Comprehensive hook for all proposal mutation operations
 *
 * Returns convenient wrapper functions for approve, reject, reassign, etc.
 *
 * @param orgId - Organization ID
 * @param options - Optional mutation options for each operation
 * @returns Object with all mutation functions
 *
 * @example
 * ```typescript
 * const actions = useProposalActions(orgId);
 *
 * // Approve a proposal
 * const handleApprove = async (proposalId: number) => {
 *   try {
 *     const result = await actions.approve(proposalId);
 *     console.log('Approved!', result);
 *   } catch (error) {
 *     console.error('Failed:', error);
 *   }
 * };
 *
 * // Reject with reason
 * const handleReject = async (proposalId: number) => {
 *   await actions.reject(proposalId, {
 *     reason: 'Needs revision',
 *   });
 * };
 *
 * // Create a new proposal
 * const handleCreate = async () => {
 *   const proposal = await actions.create(request);
 * };
 * ```
 */
export function useProposalActions(
  orgId: number,
  options?: {
    onCreate?: Omit<
      UseMutationOptions<
        ProposalDetail,
        ProposalAPIError,
        CreateProposalRequest
      >,
      'mutationFn'
    >;
    onApprove?: Omit<
      UseMutationOptions<
        ProposalDetail,
        ProposalAPIError,
        { proposalId: number }
      >,
      'mutationFn'
    >;
    onReject?: Omit<
      UseMutationOptions<
        ProposalDetail,
        ProposalAPIError,
        { proposalId: number; request?: RejectProposalRequest }
      >,
      'mutationFn'
    >;
    onReassign?: Omit<
      UseMutationOptions<
        ProposalDetail,
        ProposalAPIError,
        { proposalId: number; request: ReassignProposalRequest }
      >,
      'mutationFn'
    >;
    onUpdate?: Omit<
      UseMutationOptions<
        ProposedAction<ActionPayload>,
        ProposalAPIError,
        { proposalId: number; actionId: number; request: UpdatePayloadRequest }
      >,
      'mutationFn'
    >;
    onDelete?: Omit<
      UseMutationOptions<void, ProposalAPIError, { proposalId: number }>,
      'mutationFn'
    >;
    onBulkReject?: Omit<
      UseMutationOptions<
        BulkRejectResponse,
        ProposalAPIError,
        BulkRejectRequest
      >,
      'mutationFn'
    >;
  }
) {
  const createMutation = useCreateProposal(orgId, options?.onCreate);
  const approveMutation = useApproveProposal(orgId, options?.onApprove);
  const rejectMutation = useRejectProposal(orgId, options?.onReject);
  const reassignMutation = useReassignProposal(orgId, options?.onReassign);
  const updateMutation = useUpdateActionPayload(orgId, options?.onUpdate);
  const deleteMutation = useDeleteProposal(orgId, options?.onDelete);
  const bulkRejectMutation = useBulkRejectProposals(
    orgId,
    options?.onBulkReject
  );

  return {
    /**
     * Create a new proposal
     *
     * @param request - Proposal creation request
     * @returns Promise with created proposal
     */
    create: async (request: CreateProposalRequest) => {
      return createMutation.mutateAsync(request);
    },

    /**
     * Approve and execute a proposal
     *
     * @param proposalId - Proposal ID
     * @param selectedActionIds - Optional array of action IDs to execute (for selective execution)
     * @param actions - Optional array of action payloads for stale data detection
     * @returns Promise with executed proposal and results
     */
    approve: async (
      proposalId: number,
      selectedActionIds?: number[],
      actions?: Array<{ id: number; payload: Record<string, unknown> }>
    ) => {
      return approveMutation.mutateAsync({
        proposalId,
        selectedActionIds,
        actions,
      });
    },

    /**
     * Reject a proposal with optional reason
     *
     * @param proposalId - Proposal ID
     * @param request - Optional rejection request
     * @returns Promise with rejected proposal
     */
    reject: async (proposalId: number, request?: RejectProposalRequest) => {
      return rejectMutation.mutateAsync({ proposalId, request });
    },

    /**
     * Reassign a proposal to a different user
     *
     * @param proposalId - Proposal ID
     * @param newAssigneeUserId - New assignee user ID
     * @returns Promise with reassigned proposal
     */
    reassign: async (proposalId: number, newAssigneeUserId: number) => {
      return reassignMutation.mutateAsync({
        proposalId,
        request: { new_assignee_user_id: newAssigneeUserId },
      });
    },

    /**
     * Update an action's payload
     *
     * @param proposalId - Proposal ID
     * @param actionId - Action ID
     * @param request - Update request with new payload
     * @returns Promise with updated proposal
     */
    updateActionPayload: async (
      proposalId: number,
      actionId: number,
      request: UpdatePayloadRequest
    ) => {
      return updateMutation.mutateAsync({ proposalId, actionId, request });
    },

    /**
     * Delete a proposal
     *
     * @param proposalId - Proposal ID
     * @returns Promise
     */
    delete: async (proposalId: number) => {
      return deleteMutation.mutateAsync({ proposalId });
    },

    /**
     * Bulk reject all proposals matching filter criteria
     *
     * @param request - Filter criteria and optional reason
     * @returns Promise with rejection counts
     */
    bulkReject: async (request: BulkRejectRequest = {}) => {
      return bulkRejectMutation.mutateAsync(request);
    },

    // Expose mutation states for loading/error handling
    mutations: {
      create: createMutation,
      approve: approveMutation,
      reject: rejectMutation,
      reassign: reassignMutation,
      update: updateMutation,
      delete: deleteMutation,
      bulkReject: bulkRejectMutation,
    },

    // Convenient state checkers
    isLoading:
      createMutation.isPending ||
      approveMutation.isPending ||
      rejectMutation.isPending ||
      reassignMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending ||
      bulkRejectMutation.isPending,

    isError:
      createMutation.isError ||
      approveMutation.isError ||
      rejectMutation.isError ||
      reassignMutation.isError ||
      updateMutation.isError ||
      deleteMutation.isError ||
      bulkRejectMutation.isError,
  };
}

/**
 * Simplified hook for approval/rejection operations only
 *
 * @param orgId - Organization ID
 * @returns Object with approve and reject functions
 *
 * @example
 * ```typescript
 * const { approve, reject, isLoading } = useProposalDecision(orgId);
 *
 * <button onClick={() => approve(proposalId)} disabled={isLoading}>
 *   Approve
 * </button>
 * <button onClick={() => reject(proposalId)} disabled={isLoading}>
 *   Reject
 * </button>
 * ```
 */
export function useProposalDecision(orgId: number) {
  const approveMutation = useApproveProposal(orgId);
  const rejectMutation = useRejectProposal(orgId);

  return {
    /**
     * Approve and execute a proposal
     *
     * @param proposalId - Proposal ID
     * @param selectedActionIds - Optional array of action IDs to execute (for selective execution)
     * @param actions - Optional array of action payloads for stale data detection
     * @returns Promise with executed proposal and results
     */
    approve: async (
      proposalId: number,
      selectedActionIds?: number[],
      actions?: Array<{ id: number; payload: Record<string, unknown> }>
    ) => {
      return approveMutation.mutateAsync({
        proposalId,
        selectedActionIds,
        actions,
      });
    },
    reject: async (proposalId: number, reason?: string) => {
      return rejectMutation.mutateAsync({
        proposalId,
        request: reason ? { reason } : undefined,
      });
    },
    isLoading: approveMutation.isPending || rejectMutation.isPending,
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    error: approveMutation.error || rejectMutation.error,
  };
}
